import React, { Component, Fragment } from 'react';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import { reduxForm, SubmissionError } from 'redux-form';
import { connect } from 'react-redux';
import { graphql } from 'react-apollo';

import { banner } from 'actions';
import { Button, Input, ActionList } from 'common/components';
import { graphQlToFormErrors } from 'common/utils';
import { numberOnly } from 'common/form-validators';
import { toCommaNumber } from 'common/formatters';
import { BANNER_IDS, ERROR_DEFAULT } from 'common/constants';
import { base64_decode } from 'util/base64';
import { MUTATION_UPDATE_CROP } from 'api/crop';
import * as normalizers from 'common/normalizers';
import './index.css';

/**
 * Form Column Header: Update accordingly when renderFields() column changes
 */
const columnHeader = [
  {
    content: (
      <Fragment>
        <div className="column is-two-thirds">Field</div>
        <div className="column is-one-third">Expected Yield (bu/acre)</div>
      </Fragment>
    ),
  },
];
export class YieldsForm extends Component {
  static propTypes = {
    // redux form
    error: PropTypes.string,
    handleSubmit: PropTypes.func.isRequired,
    valid: PropTypes.bool.isRequired,
    pristine: PropTypes.bool.isRequired,
    submitting: PropTypes.bool.isRequired,
    // map dispatch
    onSubmit: PropTypes.func.isRequired,

    // manually added
    closeForm: PropTypes.func.isRequired,
    crops: PropTypes.arrayOf(
      PropTypes.shape({
        cropId: PropTypes.string,
        fieldName: PropTypes.string,
        yield: PropTypes.number,
      })
    ),
    style: PropTypes.object,

    // from graphQL
    loading: PropTypes.bool,
  };

  /**
   * Limit input to 2 digits
   * @function normalizeYield
   * @memberof YieldsForm
   */
  normalizeYield = value =>
    normalizers.limitDigits(normalizers.onlyNumbers(value), 13, 2);

  /**
   * @function renderFields
   * @memberof YieldsForm
   * @returns {Object} - with content {React.Dom}
   */
  renderFields = crop => {
    const { submitting } = this.props;

    return {
      content: (
        <Fragment key={crop.cropId}>
          <div className="column is-tight is-two-thirds">
            <p>
              {crop.fieldName}
              {crop.harvested && (
                <small className="text--secondary"> Harvested</small>
              )}
            </p>
          </div>
          <div className="column is-tight is-one-third">
            <Input
              name={crop.cropId}
              disabled={submitting || crop.harvested}
              validate={[numberOnly]}
              normalize={this.normalizeYield}
              format={toCommaNumber}
            />
          </div>
        </Fragment>
      ),
    };
  };

  render() {
    const {
      onSubmit,
      closeForm,
      error,
      handleSubmit,
      valid,
      pristine,
      submitting,
      crops,
      style,
    } = this.props;

    return (
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="is-flex-widescreen yieldsform"
      >
        <div className="column is-two-thirds-widescreen">
          {crops && crops.length > 0 && (
            <Fragment>
              <p>Update expected yield for the crops growing in the field.</p>
              <ActionList
                theme={['columns', 'columns__header']}
                cards={columnHeader}
              />
              <ActionList
                theme={['columns', 'bordered']}
                cards={crops.map(this.renderFields)}
                style={style}
              />
            </Fragment>
          )}
        </div>

        <footer className="column is-one-third-widescreen">
          {error && <p className="text--error">{error}</p>}

          <fieldset className={'field text--center'}>
            <Button
              type="submit"
              className="btn btn--primary gap-top-small"
              disabled={!valid || pristine || submitting}
            >
              Update
            </Button>
          </fieldset>

          <fieldset className={'field text--center'}>
            <Button
              type="cancel"
              className="btn"
              onClick={closeForm}
              disabled={submitting}
            >
              Cancel
            </Button>
          </fieldset>
        </footer>
      </form>
    );
  }
}

const formSettings = { form: 'yieldsform', enableReinitialize: true };
const mapDispatch = (dispatch, props) => ({
  onSubmit: async values => {
    const keys = Object.keys(values);
    let _error = null;
    await Promise.all(
      keys.map(async key => {
        if (Number(values[key]) === props.initialValues[key]) {
          return;
        }

        const vars = {
          expectedYield: {
            size: Number(values[key]),
            type: 'bushels', // BE has default values too
            per: 'acres', // BE has default values too
          },
        };

        const res = await props.onUpdateCrop(base64_decode(key), vars);

        if (res && res.data && res.data.updateCropForField) {
          dispatch(banner.display(BANNER_IDS.APP, 'YIELD UPDATED', false));
        } else if (res && res.errors) {
          _error = res.errors[0] ? res.errors[0].message : ERROR_DEFAULT;
          throw new SubmissionError({ _error });
        } else {
          throw new SubmissionError({ _error: ERROR_DEFAULT });
        }
      })
    );

    // refetch at dashboard HOC level to update all
    if (!_error) {
      props.refetch();
      props.closeForm();
    }
  },
});

export default compose(
  graphql(MUTATION_UPDATE_CROP, {
    props: props => ({
      onUpdateCrop: (id, fieldCropUpdateInput) => {
        return props
          .mutate({
            variables: { id, fieldCropUpdateInput },
          })
          .then(res => res)
          .catch(graphQlToFormErrors);
      },
      ...props,
    }),
  }),

  connect(
    null,
    mapDispatch
  ),
  reduxForm(formSettings)
)(YieldsForm);



// WEBPACK FOOTER //
// ./src/containers/YieldsForm/index.js